// The in-memory API's Parity P1 wave 1 routes (services/api routes/me.ts prefs, routes/work/
// task-layout.ts, routes/work/task-detail.ts), with the real routes' rules where the app sees
// them: whitelisted pref keys, layouts versioned per board / space (`base_version` stale → 409,
// the newest five in history, `layout: null` resets a board to its space default), field rules,
// a field's history from `task.field_changed`, watchers (follows, assignee seat holders, the
// creator), task observations (corrections supersede), and over the Work twin: `tasks.get` with
// the P1 extensions and `tasks.setStatus` refusing a done status while a field required on
// completion is empty (422 `required_missing`).
//
// Seed (Acme, design §3.5 on the twin's own data): "Offer letter for Tomás R." on Hiring
// pipeline gets a layout with an "Agent fields" section holding a titled group, a metric with
// six observations, an always-shown field, history by a person and an agent,
// three watchers; the owner's prefs open the rail and fold Delivery.
import { prefSchema } from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import { COMPANY_IDS, type FakeDe, uid } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";
import type { WorkBridge } from "./fake-work";

type Layout = OperationOutput<"taskLayout.board.get">["effective"];
type Version = OperationOutput<"taskLayout.board.history">["data"][number];
type Rule = OperationOutput<"fields.rule">;
type Observation = OperationOutput<"tasks.observations.list">["data"][number];
type Actor = Version["saved_by"];

// The real whitelist and value schemas (HOME-DASH: one source, so the twin cannot drift).
const PREF_KEYS = { has: (key: string) => prefSchema(key) !== null };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const FAKE_DEFAULT_LAYOUT: Layout = {
  version: 1,
  sections: [
    {
      id: "overview",
      title: "Overview",
      columns: [{ width: 12, items: ["description", "checklists"] }],
    },
    {
      id: "delivery",
      title: "Delivery",
      columns: [{ width: 12, items: ["subtasks", "dependencies", "links", "files", "time"] }],
    },
    { id: "metrics", title: "Metrics", columns: [{ width: 12, items: ["metrics"] }] },
  ],
  hidden: [],
};

/**
 * The API reads prefs through `workAccess`, which refuses a person who is only a client viewer
 * (403); the twin does the same.
 */
const clientViewerOnly = (p: Principal) =>
  p.roles.length > 0 && p.roles.every((r) => r === "client_viewer");

/** Stable JSON (sorted keys), as the API's `canonical`. */
const sortKeys = (v: unknown): unknown =>
  Array.isArray(v)
    ? v.map(sortKeys)
    : v && typeof v === "object"
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, x]) => [k, sortKeys(x)]),
        )
      : v;

const empty = (v: unknown) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export function createP1Fake(deps: {
  now: () => Date;
  work: () => WorkBridge | null;
  desOf: (companyId: string) => FakeDe[];
  /** The handler the composed map had before P1's (Work's `tasks.get`, `tasks.setStatus`). */
  previous: (id: RouteId) => ComposedHandler | undefined;
}): Partial<Record<RouteId, ComposedHandler>> {
  let seq = 0;
  const next = () => uid(0x7e10, ++seq);
  const stamp = () => deps.now().toISOString();
  const prefs = new Map<string, { value: unknown; updated_at: string }>();
  /** `${kind}:${id}` → versions, oldest first. */
  const layouts = new Map<string, Version[]>();
  const rules = new Map<string, Rule>();
  const observations = new Map<string, Observation[]>();
  const seeded = new Set<string>();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const actorOf = (p: Principal): Actor => {
    const m = membershipOf(p);
    return m
      ? { kind: "person", membership_id: m }
      : { kind: "agency_user", user_id: p.me.user.id, auth_version: 0 };
  };
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  const state = (p: Principal) => deps.work()?.stateOf(p.company.id) ?? null;
  const taskOf = (p: Principal, id: string) =>
    state(p)?.tasks.find((t) => t.id === id && !t.deleted) ?? null;
  const boardOf = (p: Principal, id: string) => state(p)?.boards.find((b) => b.id === id) ?? null;
  const fieldsOf = (p: Principal, boardId: string) =>
    (state(p)?.fields ?? []).filter((f) => f.board_id === boardId);
  const ruleOf = (fieldId: string): Rule =>
    rules.get(fieldId) ?? {
      field_def_id: fieldId,
      required_on: "none",
      always_show: false,
      show_on_card: false,
    };

  /** Seat → who holds it (DEs and their supervisors: the twin's seat holders). */
  const holders = (companyId: string) => {
    const out = new Map<
      string,
      { membership_id: string; name: string; kind: "person" | "agent" }
    >();
    for (const d of deps.desOf(companyId)) {
      out.set(d.seat_id, { membership_id: d.membership_id, name: d.display_name, kind: "agent" });
      out.set(d.supervisor.seat_id, {
        membership_id: d.supervisor.membership_id,
        name: d.supervisor.name,
        kind: "person",
      });
    }
    return out;
  };
  const nameOf = (p: Principal, membership: string) => {
    for (const h of holders(p.company.id).values()) if (h.membership_id === membership) return h;
    if (membership === membershipOf(p))
      return { membership_id: membership, name: p.me.user.name ?? "You", kind: "person" as const };
    return { membership_id: membership, name: "A member", kind: "person" as const };
  };

  const versionsOf = (kind: "board" | "space", id: string) => layouts.get(`${kind}:${id}`) ?? [];
  const current = (kind: "board" | "space", id: string) => versionsOf(kind, id).at(-1) ?? null;
  const save = (p: Principal, kind: "board" | "space", id: string, layout: Layout | null) => {
    const list = versionsOf(kind, id);
    const v: Version = {
      version: (list.at(-1)?.version ?? 0) + 1,
      layout,
      saved_by: actorOf(p),
      saved_at: stamp(),
    };
    layouts.set(`${kind}:${id}`, [...list, v]);
  };

  function boardState(p: Principal, boardId: string) {
    seed(p);
    const b = boardOf(p, boardId);
    if (!b) return null;
    const own = current("board", b.id);
    const space = current("space", b.space_id);
    const effective = own?.layout ?? space?.layout ?? FAKE_DEFAULT_LAYOUT;
    return {
      board_id: b.id,
      space_id: b.space_id,
      layout: own?.layout ?? null,
      version: own?.version ?? 0,
      effective,
      source: own?.layout
        ? ("board" as const)
        : space?.layout
          ? ("space" as const)
          : ("default" as const),
      fields: fieldsOf(p, b.id).map((f) => ruleOf(f.id)),
      can_edit: p.manager,
    };
  }

  function spaceState(p: Principal, spaceId: string) {
    seed(p);
    const s = state(p);
    if (!s?.spaces.some((x) => x.id === spaceId)) return null;
    const own = current("space", spaceId);
    return {
      space_id: spaceId,
      layout: own?.layout ?? null,
      version: own?.version ?? 0,
      effective: own?.layout ?? FAKE_DEFAULT_LAYOUT,
      boards: s.boards
        .filter((b) => b.space_id === spaceId && !b.archived_at)
        .map((b) => ({
          board_id: b.id,
          name: b.name,
          uses_default: !current("board", b.id)?.layout,
        })),
      can_edit: p.manager,
    };
  }

  function validLayout(l: unknown): l is Layout {
    if (!l || typeof l !== "object") return false;
    const x = l as Layout;
    return (
      x.version === 1 &&
      Array.isArray(x.sections) &&
      x.sections.length <= 20 &&
      Array.isArray(x.hidden) &&
      x.sections.every(
        (s) =>
          typeof s.id === "string" &&
          Array.isArray(s.columns) &&
          s.columns.length >= 1 &&
          s.columns.length <= 4 &&
          s.columns.every((c) => c.width >= 2 && c.width <= 12 && Array.isArray(c.items)),
      )
    );
  }

  function watchersOf(p: Principal, taskId: string) {
    const s = state(p);
    const t = taskOf(p, taskId);
    if (!s || !t) return null;
    const by = new Map<
      string,
      {
        membership_id: string;
        name: string;
        kind: "person" | "agent";
        reasons: ("follow" | "assignee" | "creator")[];
      }
    >();
    const add = (m: string, reason: "follow" | "assignee" | "creator") => {
      const who = nameOf(p, m);
      const row = by.get(m) ?? { ...who, reasons: [] };
      if (!row.reasons.includes(reason)) row.reasons.push(reason);
      by.set(m, row);
    };
    for (const f of s.follows)
      if (f.subject_kind === "task" && f.subject_id === taskId) add(f.membership_id, "follow");
    const seats = holders(p.company.id);
    for (const seat of t.assignee_seat_ids) {
      const h = seats.get(seat);
      if (h) add(h.membership_id, "assignee");
    }
    if (t.created_by_seat_id) {
      const h = seats.get(t.created_by_seat_id);
      if (h) add(h.membership_id, "creator");
    }
    const me = membershipOf(p);
    const mine = me ? by.get(me) : undefined;
    return {
      data: [...by.values()],
      me: { following: Boolean(mine), explicit: Boolean(mine?.reasons.includes("follow")) },
    };
  }

  function requiredMissing(p: Principal, taskId: string) {
    const t = taskOf(p, taskId);
    if (!t) return [];
    return fieldsOf(p, t.board_id)
      .filter((f) => !["formula", "rollup"].includes(f.type))
      .filter((f) => ["complete", "both"].includes(ruleOf(f.id).required_on))
      .filter((f) => empty(t.fields[f.id]))
      .map((f) => ({ field_def_id: f.id, name: f.name, required_on: ruleOf(f.id).required_on }));
  }

  /**
   * The API's create and move checks: the board's fields required on `stages` (computed ones
   * never are) that `values` leave empty, as the 422 `required_missing` refusal, or null.
   */
  function refuseMissing(
    p: Principal,
    boardId: string,
    stages: readonly string[],
    values: Record<string, unknown>,
    detail: string,
  ) {
    const missing = fieldsOf(p, boardId)
      .filter((f) => !["formula", "rollup"].includes(f.type))
      .filter((f) => stages.includes(ruleOf(f.id).required_on))
      .filter((f) => empty(values[f.id]));
    if (!missing.length) return null;
    return problem(422, "required_missing", "Required fields are empty", detail, {
      missing: missing.map((f) => f.id),
    });
  }
  const isDone = (p: Principal, boardId: string, status: string | undefined) =>
    boardOf(p, boardId)?.status_set.find((s) => s.key === status)?.done === true;

  /** The demo's P1 detail on one Acme task (once per company). */
  function seed(p: Principal) {
    if (seeded.has(p.company.id)) return;
    seeded.add(p.company.id);
    if (p.company.id !== COMPANY_IDS.acme) return;
    const s = state(p);
    const board = s?.boards.find((b) => b.name === "Hiring pipeline");
    const task = s?.tasks.find((t) => t.title === "Offer letter for Tomás R.");
    if (!s || !board || !task) return;
    const fields = fieldsOf(p, board.id);
    const source = fields.find((f) => f.name === "Source");
    const salary = fields.find((f) => f.name === "Salary band");
    const owner = membershipOf(p);
    const at = (minutesAgo: number) =>
      new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();
    layouts.set(`board:${board.id}`, [
      {
        version: 1,
        layout: {
          version: 1,
          sections: [
            FAKE_DEFAULT_LAYOUT.sections[0] as Layout["sections"][number],
            {
              id: "agent-fields",
              title: "Agent fields",
              columns: [
                {
                  width: 6,
                  items: [
                    {
                      group: { title: "Agent notes", blocks: source ? [`field:${source.id}`] : [] },
                    },
                  ],
                },
                { width: 6, items: ["metrics"] },
              ],
            },
            {
              id: "delivery",
              title: "Delivery",
              columns: [
                { width: 12, items: ["subtasks", "dependencies", "links", "files", "time"] },
              ],
            },
          ],
          hidden: [],
        },
        saved_by: owner ? { kind: "person", membership_id: owner } : { kind: "system" },
        saved_at: at(60 * 24 * 3),
      },
    ]);
    // "Salary band" always shows; no field is required here (a board-wide rule would refuse
    // every other test's completion on the board): the e2e sets one through the editor.
    if (salary)
      rules.set(salary.id, {
        field_def_id: salary.id,
        required_on: "none",
        always_show: true,
        show_on_card: false,
      });
    const riley = deps.desOf(p.company.id).find((d) => d.display_name.startsWith("Riley"));
    const values = [12, 18, 15, 24, 31, 38];
    observations.set(
      task.id,
      values.map((value, i) => ({
        id: next(),
        task_id: task.id,
        metric: "Candidate response %",
        value,
        unit: "%",
        note: i === values.length - 1 ? "After the second follow-up" : null,
        observed_at: at((values.length - i) * 60 * 24),
        supersedes_id: null,
        superseded: false,
        actor: riley
          ? { kind: "digital_employee", membership_id: riley.membership_id }
          : { kind: "system" },
        created_at: at((values.length - i) * 60 * 24),
      })),
    );
    const bridge = deps.work();
    if (source && bridge) {
      if (owner)
        bridge.recordTaskEvent(
          p.company.id,
          task.id,
          "task.field_changed",
          { board_id: board.id, field_def_id: source.id, from: null, to: "referral" },
          { kind: "person", membership_id: owner },
        );
      if (riley)
        bridge.recordTaskEvent(
          p.company.id,
          task.id,
          "task.field_changed",
          { board_id: board.id, field_def_id: source.id, from: "referral", to: "linkedin" },
          { kind: "digital_employee", membership_id: riley.membership_id },
        );
    }
    // Three watchers: two DEs and the owner follow the task.
    const followers = [
      ...deps
        .desOf(p.company.id)
        .slice(0, 2)
        .map((d) => d.membership_id),
      ...(owner ? [owner] : []),
    ];
    for (const m of followers)
      if (!s.follows.some((f) => f.membership_id === m && f.subject_id === task.id))
        s.follows.push({
          company_id: p.company.id,
          membership_id: m,
          subject_kind: "task",
          subject_id: task.id,
          level: "all",
          actor_kind: "person",
          actor_membership_id: owner,
          created_at: at(600),
          updated_at: at(600),
        } as (typeof s.follows)[number]);
    if (owner)
      prefs.set(`${p.company.id}:${p.me.user.id}:task_drawer`, {
        value: { rail: "open", collapsed: ["delivery"] },
        updated_at: at(60),
      });
  }

  const prefKey = (p: Principal, key: string) => `${p.company.id}:${p.me.user.id}:${key}`;
  const bodyOf = (ctx: Ctx) => (ctx.body ?? {}) as Record<string, unknown>;

  const stateFor = (kind: "board" | "space", p: Principal, id: string) =>
    kind === "board" ? boardState(p, id) : spaceState(p, id);
  const layoutPut =
    (kind: "board" | "space"): Handler =>
    ({ principal, params, body }) => {
      const id = params.id ?? "";
      if (!reader(principal)) return notFound();
      const st = stateFor(kind, principal, id);
      if (!st) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden", "board managers only");
      const b = (body ?? {}) as { layout?: unknown; base_version?: unknown };
      if (b.layout !== null && !validLayout(b.layout))
        return validation("layout: not a task layout");
      if (b.base_version !== st.version)
        return problem(409, "conflict", "Conflict", "the layout was saved since you opened it");
      save(principal, kind, id, (b.layout ?? null) as Layout | null);
      return json(stateFor(kind, principal, id));
    };
  const layoutRestore =
    (kind: "board" | "space"): Handler =>
    ({ principal, params, body }) => {
      const id = params.id ?? "";
      if (!reader(principal)) return notFound();
      const st = stateFor(kind, principal, id);
      if (!st) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden", "board managers only");
      const b = (body ?? {}) as { version?: number; base_version?: number };
      const v = versionsOf(kind, id).find((x) => x.version === b.version);
      if (!v) return validation("version: no such version");
      if (b.base_version !== st.version) return problem(409, "conflict", "Conflict");
      save(principal, kind, id, v.layout);
      return json(stateFor(kind, principal, id));
    };
  const history =
    (kind: "board" | "space"): Handler =>
    ({ principal, params }) => {
      const id = params.id ?? "";
      if (!reader(principal)) return notFound();
      if (!stateFor(kind, principal, id)) return notFound();
      return json({ data: [...versionsOf(kind, id)].reverse().slice(0, 5) });
    };

  return {
    "me.prefs.get": ({ principal, params }) => {
      if (!principal) return notFound();
      if (clientViewerOnly(principal)) return problem(403, "forbidden", "Forbidden");
      seed(principal);
      if (!PREF_KEYS.has(params.key ?? "")) return validation("key: not a preference");
      const row = prefs.get(prefKey(principal, params.key ?? ""));
      return json({
        key: params.key,
        value: row?.value ?? null,
        updated_at: row?.updated_at ?? null,
      });
    },
    "me.prefs.put": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (clientViewerOnly(principal)) return problem(403, "forbidden", "Forbidden");
      if (!PREF_KEYS.has(params.key ?? "")) return validation("key: not a preference");
      const value = (body as { value?: unknown } | null)?.value;
      if (
        value === undefined ||
        typeof value !== "object" ||
        value === null ||
        Array.isArray(value)
      )
        return validation("value: an object");
      const parsed = prefSchema(params.key ?? "")?.safeParse(value);
      if (parsed && !parsed.success)
        return validation(
          parsed.error.issues.map((i) => `value.${i.path.join(".")}: ${i.message}`).join("; "),
        );
      // An unchanged value keeps its stamp (the API writes nothing then).
      const cur = prefs.get(prefKey(principal, params.key ?? ""));
      if (cur && JSON.stringify(sortKeys(cur.value)) === JSON.stringify(sortKeys(parsed?.data)))
        return json({ key: params.key, ...cur });
      const row = { value: parsed?.data ?? value, updated_at: stamp() };
      prefs.set(prefKey(principal, params.key ?? ""), row);
      return json({ key: params.key, ...row });
    },
    "taskLayout.board.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const st = boardState(principal, params.id ?? "");
      return st ? json(st) : notFound();
    },
    "taskLayout.board.put": layoutPut("board"),
    "taskLayout.board.history": history("board"),
    "taskLayout.board.restore": layoutRestore("board"),
    "taskLayout.space.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const st = spaceState(principal, params.id ?? "");
      return st ? json(st) : notFound();
    },
    "taskLayout.space.put": layoutPut("space"),
    "taskLayout.space.history": history("space"),
    "taskLayout.space.restore": layoutRestore("space"),
    "fields.rule": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const f = state(principal)?.fields.find((x) => x.id === params.id);
      if (!f) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden", "board managers only");
      const b = (body ?? {}) as Partial<Rule>;
      if (
        b.required_on !== undefined &&
        !["none", "create", "complete", "both"].includes(b.required_on)
      )
        return validation("required_on: none, create, complete or both");
      const r = { ...ruleOf(f.id), ...b, field_def_id: f.id };
      rules.set(f.id, r);
      return json(r);
    },
    "tasks.fieldHistory": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      seed(principal);
      if (!taskOf(principal, params.id ?? "")) return notFound();
      const field = url.searchParams.get("field_id");
      if (!field || !UUID.test(field)) return validation("field_id is required");
      const rows = (state(principal)?.events ?? [])
        .filter(
          (e) =>
            e.type === "task.field_changed" &&
            e.subject.id === params.id &&
            (e.payload as { field_def_id?: string }).field_def_id === field,
        )
        .map((e) => {
          const pl = e.payload as { from?: unknown; to?: unknown };
          return {
            event_id: e.id,
            field_def_id: field,
            from: pl.from ?? null,
            to: pl.to ?? null,
            at: e.occurred_at,
            actor: e.actor,
          };
        })
        .reverse()
        .slice(0, 100);
      return json({ data: rows });
    },
    "tasks.watchers": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      seed(principal);
      const w = watchersOf(principal, params.id ?? "");
      return w ? json(w) : notFound();
    },
    "tasks.observations.list": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      seed(principal);
      if (!taskOf(principal, params.id ?? "")) return notFound();
      const metric = url.searchParams.get("metric");
      const rows = (observations.get(params.id ?? "") ?? []).filter(
        (o) => !metric || o.metric.toLowerCase() === metric.toLowerCase(),
      );
      return json({ data: rows });
    },
    "tasks.observations.create": async (ctx) => {
      const { principal, params, body } = ctx;
      if (!reader(principal)) return notFound();
      const t = taskOf(principal, params.id ?? "");
      if (!t) return notFound();
      const b = bodyOf({ body } as Ctx) as {
        metric?: string;
        value?: number;
        unit?: string;
        note?: string;
        observed_at?: string;
        supersedes_id?: string;
      };
      if (!b.metric?.trim() || typeof b.value !== "number" || !Number.isFinite(b.value))
        return validation("metric and a numeric value are required");
      const list = observations.get(t.id) ?? [];
      const metric = b.metric.trim().toLowerCase();
      if (b.supersedes_id) {
        const old = list.find((o) => o.id === b.supersedes_id && o.metric.toLowerCase() === metric);
        if (!old || old.superseded)
          return problem(409, "conflict", "Conflict", "that observation is not the live one");
        old.superseded = true;
      }
      const row: Observation = {
        id: next(),
        task_id: t.id,
        metric: b.metric.trim(),
        value: b.value,
        unit: b.unit ?? null,
        note: b.note ?? null,
        observed_at: b.observed_at ?? stamp(),
        supersedes_id: b.supersedes_id ?? null,
        superseded: false,
        actor: actorOf(principal),
        created_at: stamp(),
      };
      observations.set(t.id, [...list, row]);
      // The number field named like the metric mirrors its newest live value (as the API does,
      // through the field writer, so the field's history records it).
      const newest = [...list, row]
        .filter((o) => !o.superseded && o.metric.toLowerCase() === metric)
        .sort((x, y) => y.observed_at.localeCompare(x.observed_at) || y.id.localeCompare(x.id))[0];
      const mirror = fieldsOf(principal, t.board_id).find(
        (f) => f.type === "number" && f.name.toLowerCase() === metric,
      );
      if (mirror && newest && t.fields[mirror.id] !== newest.value)
        await deps.previous("tasks.setField")?.({
          ...ctx,
          params: { ...params, field_id: mirror.id },
          body: { value: newest.value },
        });
      return json(row, 201);
    },
    "tasks.create": async (ctx) => {
      const p = ctx.principal;
      const b = (ctx.body ?? {}) as {
        board_id?: string;
        status?: string;
        fields?: Record<string, unknown>;
      };
      const board = p && b.board_id ? boardOf(p, b.board_id) : null;
      if (p && board) {
        seed(p);
        const status = b.status ?? board.status_set[0]?.key;
        const stages = isDone(p, board.id, status)
          ? ["create", "complete", "both"]
          : ["create", "both"];
        const bad = refuseMissing(
          p,
          board.id,
          stages,
          b.fields ?? {},
          "Fill these to create the task",
        );
        if (bad) return bad;
      }
      return (await deps.previous("tasks.create")?.(ctx)) ?? notFound();
    },
    "tasks.move": async (ctx) => {
      const p = ctx.principal;
      const t = p ? taskOf(p, ctx.params.id ?? "") : null;
      const b = (ctx.body ?? {}) as { board_id?: string; status?: string };
      const target = p && b.board_id ? boardOf(p, b.board_id) : null;
      if (p && t && target) {
        seed(p);
        // The status it lands in: the one asked for, else the same key when the target has it.
        const to = b.status ?? t.status;
        if (isDone(p, target.id, to)) {
          const bad = refuseMissing(
            p,
            target.id,
            ["complete", "both"],
            t.fields,
            "Fill these before completing",
          );
          if (bad) return bad;
        }
      }
      return (await deps.previous("tasks.move")?.(ctx)) ?? notFound();
    },
    // Over the Work twin: the detail's P1 extensions, and the completion check.
    "tasks.get": async (ctx) => {
      const res = await deps.previous("tasks.get")?.(ctx);
      if (!res?.ok || !ctx.principal) return res ?? notFound();
      const p = ctx.principal;
      seed(p);
      const t = (await res.json()) as {
        id: string;
        board_id: string;
        estimate_minutes: number | null;
      };
      const st = boardState(p, t.board_id);
      return json({
        ...t,
        extensions: {
          estimate_minutes: t.estimate_minutes,
          watchers: watchersOf(p, t.id)?.data ?? [],
          required_missing: requiredMissing(p, t.id),
          layout_sections: (st?.effective.sections ?? [])
            .filter((s) => !s.hidden)
            .map((s) => ({
              id: s.id,
              title: s.title ?? null,
              blocks: s.columns.flatMap((c) =>
                c.items.flatMap((i) => (typeof i === "string" ? [i] : i.group.blocks)),
              ),
            })),
        },
      });
    },
    "tasks.setStatus": async (ctx) => {
      const p = ctx.principal;
      const t = p ? taskOf(p, ctx.params.id ?? "") : null;
      if (p && t) {
        seed(p);
        const to = (ctx.body as { status?: string } | null)?.status;
        const b = boardOf(p, t.board_id);
        const done = b?.status_set.find((s) => s.key === to)?.done === true;
        const missing = done ? requiredMissing(p, t.id) : [];
        if (missing.length)
          return problem(422, "required_missing", "Required fields are empty", undefined, {
            missing: missing.map((m) => m.field_def_id),
          });
      }
      return (await deps.previous("tasks.setStatus")?.(ctx)) ?? notFound();
    },
  };
}
